import { useEffect, useMemo, useState } from "react";

import {
  type DataPointSample,
  type Equipment,
  type FindingSeverity,
} from "@/lib/api/building";
import {
  getZoneKpisSummary,
  type KBZoneSummary,
  type ZoneKpis,
  type ZoneKpisSummaryEntry,
} from "@/lib/api/agent";
import { type Zone } from "@/lib/building-domain";
import { inputGlowWrap, inputGlowInner } from "@/lib/card-styles";
import { indexZoneKpisByZone } from "@/lib/zone-kpi";
import {
  next_open_row_id,
  reconcile_open_row_id,
} from "@/components/building/shared/one-open-accordion";

import { ZoneTile } from "./ZoneTile";
import { ZoneDetailPanel } from "./ZoneDetailPanel";
import { ZoneForm } from "./ZoneForm";

// ── Interfaces ────────────────────────────────────────────────────────────────

interface Props {
  buildingId: string;
  zones: Zone[];
  zone_equipment: Map<string, Equipment[]>;
  kb_zone_types: KBZoneSummary[];
  // Used only by ZoneDetailPanel (expanded card). ZoneTile never receives samples.
  live_signals: Map<string, Record<string, DataPointSample[]>>;
  kb_labels: Record<string, string>;
  operator: boolean;
  on_create: (data: {
    name: string;
    floor: number;
    zone_type: string | null;
    description: string;
  }) => Promise<void>;
  on_update: (
    zone_id: string,
    data: {
      name?: string;
      floor?: number;
      icon?: string;
      zone_type?: string | null;
      description?: string;
    },
  ) => Promise<void>;
  on_delete: (zone_id: string) => Promise<void>;
  findings_severity: Map<string, FindingSeverity>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMPTY_SIGNALS: Record<string, DataPointSample[]> = {};

function floor_label(floor: number): string {
  if (floor === 0) return "Rakennustaso";
  return `Kerros ${floor}`;
}

// ── ZoneGrid ──────────────────────────────────────────────────────────────────

export function ZoneGrid({
  buildingId,
  zones,
  zone_equipment,
  kb_zone_types,
  live_signals,
  kb_labels,
  operator,
  on_create,
  on_update,
  on_delete,
  findings_severity,
}: Props) {
  const [open_id, set_open_id] = useState<string | null>(null);
  const [open_zone_id, set_open_zone_id] = useState<string | null>(null);
  const [accordion_initialized, set_accordion_initialized] = useState(false);
  const [add_open, set_add_open] = useState(false);
  const [filter_floor, set_filter_floor] = useState<string>("");
  const [search, set_search] = useState("");
  const [kpis_map, set_kpis_map] = useState<Map<string, ZoneKpis>>(new Map());
  const [kpis_pending, set_kpis_pending] = useState(!operator);

  useEffect(() => {
    if (operator || zones.length === 0) {
      set_kpis_map(new Map());
      set_kpis_pending(false);
      return;
    }
    let cancelled = false;
    set_kpis_pending(true);
    void getZoneKpisSummary(buildingId)
      .catch((): ZoneKpisSummaryEntry[] => [])
      .then((kpi_entries) => {
        if (cancelled) return;
        set_kpis_map(indexZoneKpisByZone(kpi_entries));
        set_kpis_pending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [buildingId, zones, operator]);

  const used_floors = useMemo(() => {
    const floors = new Set<number>();
    for (const z of zones) floors.add(z.floor);
    return [...floors].sort((a, b) => {
      if (a === 0) return 1;
      if (b === 0) return -1;
      return b - a;
    });
  }, [zones]);

  const visible = useMemo(() => {
    return zones.filter((z) => {
      if (filter_floor !== "" && String(z.floor) !== filter_floor) return false;
      if (search) {
        return z.name.toLowerCase().includes(search.toLowerCase());
      }
      return true;
    });
  }, [zones, filter_floor, search]);

  const floor_groups = useMemo(() => {
    const map = new Map<number, Zone[]>();
    for (const z of visible) {
      if (!map.has(z.floor)) map.set(z.floor, []);
      map.get(z.floor)!.push(z);
    }
    const entries = [...map.entries()];
    entries.sort(([a], [b]) => {
      if (a === 0) return 1;
      if (b === 0) return -1;
      return b - a;
    });
    return entries;
  }, [visible]);

  const visible_ids = useMemo(
    () =>
      floor_groups.flatMap(([, floor_zones]) => floor_zones.map((z) => z.id)),
    [floor_groups],
  );

  useEffect(() => {
    if (!operator) return;
    const next = reconcile_open_row_id(
      open_zone_id,
      visible_ids,
      accordion_initialized,
    );
    if (next.open_id !== open_zone_id) {
      set_open_zone_id(next.open_id);
    }
    if (next.initialized !== accordion_initialized) {
      set_accordion_initialized(next.initialized);
    }
  }, [operator, visible_ids, open_zone_id, accordion_initialized]);

  return (
    <div className="flex flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className={`min-w-40 flex-1 ${inputGlowWrap}`}>
          <div className={inputGlowInner}>
            <input
              value={search}
              onChange={(e) => set_search(e.target.value)}
              placeholder="Hae nimellä…"
              className="w-full bg-transparent px-3 py-1.5 text-sm placeholder:text-muted-foreground outline-none"
            />
          </div>
        </div>
        {used_floors.length > 1 && (
          <select
            value={filter_floor}
            onChange={(e) => set_filter_floor(e.target.value)}
            className="rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60"
          >
            <option value="">Kaikki kerrokset</option>
            {used_floors.map((f) => (
              <option key={f} value={String(f)}>
                {floor_label(f)}
              </option>
            ))}
          </select>
        )}
        {operator && (
          <button
            onClick={() => set_add_open((v) => !v)}
            className="ml-auto rounded-lg border border-dashed border-border/40 px-3 py-1.5 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
          >
            + Lisää vyöhyke
          </button>
        )}
      </div>

      {add_open && (
        <ZoneForm
          on_submit={async (data) => {
            await on_create(data);
            set_add_open(false);
          }}
          on_cancel={() => set_add_open(false)}
        />
      )}

      {visible.length === 0 && !add_open ? (
        <p className="mt-8 text-center text-sm text-muted-foreground">
          {zones.length === 0
            ? "Ei vyöhykkeitä. Lisää vyöhyke yllä olevalla painikkeella."
            : "Ei tuloksia haulla."}
        </p>
      ) : (
        <div className="flex flex-col gap-5">
          {floor_groups.map(([floor, floor_zones]) => (
            <div key={floor}>
              {floor_groups.length > 1 && (
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                  {floor_label(floor)}
                </p>
              )}
              {operator ? (
                <div className="flex flex-col gap-2">
                  {floor_zones.map((z) => (
                    <ZoneTile
                      key={z.id}
                      zone={z}
                      kb_zone_types={kb_zone_types}
                      expanded={open_zone_id === z.id}
                      on_toggle={() =>
                        set_open_zone_id(next_open_row_id(open_zone_id, z.id))
                      }
                      operator={operator}
                      max_severity={findings_severity.get(z.id)}
                      kpis={null}
                      serving_equipment={zone_equipment.get(z.id) ?? []}
                      on_update={on_update}
                      on_delete={async (id) => {
                        if (open_zone_id === id) set_open_zone_id(null);
                        await on_delete(id);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 items-stretch gap-3 lg:grid-cols-3">
                  {floor_zones.map((z) => (
                    <ZoneTile
                      key={z.id}
                      zone={z}
                      kb_zone_types={kb_zone_types}
                      expanded={open_id === z.id}
                      on_toggle={() =>
                        set_open_id(open_id === z.id ? null : z.id)
                      }
                      operator={operator}
                      max_severity={findings_severity.get(z.id)}
                      kpis={kpis_map.get(z.id) ?? null}
                      kpis_pending={kpis_pending}
                      serving_equipment={zone_equipment.get(z.id) ?? []}
                      on_update={on_update}
                      on_delete={async (id) => {
                        if (open_id === id) set_open_id(null);
                        await on_delete(id);
                      }}
                    />
                  ))}
                  {open_id !== null &&
                    floor_zones.some((z) => z.id === open_id) &&
                    (() => {
                      const active = floor_zones.find((z) => z.id === open_id);
                      if (!active) return null;
                      return (
                        <ZoneDetailPanel
                          key={open_id}
                          buildingId={buildingId}
                          zone={active}
                          equipment={zone_equipment.get(active.id) ?? []}
                          live_signals={
                            live_signals.get(active.id) ?? EMPTY_SIGNALS
                          }
                          signal_labels={kb_labels}
                          on_close={() => set_open_id(null)}
                          operator={operator}
                        />
                      );
                    })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
